<template>
	<BaseLayout class="ira-landing" :include-padding="false" background-color-class="bg-color-white">
		<AssetPageHero :hero="iraHeroDetails" :form-config="leadCaptureConfig">
			<template #hero-image>
				<div
					:class="[
						'col-6-md mt-225-smo hero-img-wrapper m-0 ',
						{ 'pl-300-md': thirdPartyAuthStore.isSignupEnabled }
					]"
				>
					<img
						v-reveal:fade-up
						:src="iraHeroDetails.imageUrl"
						alt="IRA hero graphic showcasing Fundrise mobile app"
						class="hero-img"
					/>
				</div>
			</template>
			<template #form>
				<IraLandingCta :should-be-centered="false" third-party-auth-theme="PRIMARY" />
			</template>
		</AssetPageHero>
		<SocialProofSectionFullWidth />
		<div class="marketing-section bg-color-bg-tertiary">
			<InfoCardsOverlap :cards="infoCardsData" />
		</div>
		<div class="marketing-section bg-color-greige">
			<div class="container">
				<h2 class="heading-2 text-align-center mb-200">Important details</h2>
				<div class="col-10-md col-6-lg ml-auto mr-auto">
					<QuestionList :question-items="faqContent" />
				</div>
			</div>
		</div>
		<CTABanner>
			<template #heading>
				<h2 class="balance-text heading-1 mb-200 text-color-white"> Invest today, get your fees waived </h2>
			</template>

			<template #icon>
				<BaseSvgIcon :fill="false" name="hd-coin-stack" width="96" height="96" class="mb-300" />
			</template>
			<template #summary>
				<div class="body-lg text-color-gray-20 mb-300">
					<div class="text-align-left">
						There are two ways to get your {{ currency(feeAmount, 0) }} annual IRA fee waived:
						<ul class="ira-cta-list">
							<li
								>Earn a one-year fee waiver when you invest {{ currency(annualThresholdLimit, 0) }} or
								more.</li
							><li
								>Secure an automatically recurring fee waiver when you achieve and maintain an account
								value over {{ currency(accountSizeThresholdLimit, 0) }}.</li
							></ul
						>
					</div>
					<p class="body-sm text-color-gray-40 text-align-center">
						Traditional and Roth accounts are eligible.
					</p>
				</div>
			</template>
			<template #link>
				<IraLandingCta
					lead-capture-classes="col-12"
					:should-be-centered="true"
					third-party-auth-theme="SECONDARY"
					third-party-auth-disclaimer-class="text-align-center muted"
				/>
			</template>
		</CTABanner>

		<template #footer>
			<IraLandingFooter />
		</template>
	</BaseLayout>
</template>

<script setup lang="ts">
import { appBasePath, appCloudfrontPath } from '@constants';
import { InfoCard, MarketingImageHero, QuestionItem } from 'types/layout';
import AssetPageHero from '@components/marketing-site/asset-page-hero.vue';
import { computed } from 'vue';
import CTABanner from '@components/layout/cta-banner.vue';
import { currency } from '@filters/shared-filters';
import InfoCardsOverlap from '@components/layout/info-cards-overlap.vue';
import IraLandingCta from '@components/ira/ira-landing-cta.vue';
import IraLandingFooter from '@components/ira/ira-landing-footer.vue';
import { LeadCaptureFormConfig } from 'types/layout/lead';
import QuestionList from '@components/layout/question-list.vue';
import SocialProofSectionFullWidth from '@components/marketing-site/social-proof-section-full-width.vue';
import { useAppStore } from '@stores/app';
import { useHead } from '@unhead/vue';
import { useIraStore } from '@stores/ira';
import { useThirdPartyAuthStore } from '@stores/third-party-auth';

const appStore = useAppStore();
const ira = useIraStore();
const thirdPartyAuthStore = useThirdPartyAuthStore();

const leadCaptureConfig: LeadCaptureFormConfig = {
	signupFlowType: 'STANDARD'
};

const infoCardsData: Array<InfoCard> = [
	{
		heading: 'Our assets',
		title: 'Diversify your retirement portfolio',
		description: [
			'Private market assets are less likely to be affected by market fluctuations, making them an excellent tool for portfolio diversification. Designed with  long-term stability and growth in mind, our assets serve as a strategic addition to any retirement savings plan.'
		],
		imageUrl: `${appCloudfrontPath}/images/ira/man-running.png`
	},
	{
		heading: 'Account options',
		title: 'Tap into tax-advantaged growth',
		description: [
			'Keep more of what you earn by opening an IRA with Fundrise. Choose a traditional IRA for tax-deferred growth or a Roth IRA for tax-free gains, all backed by our diversified portfolio of private assets. Easily roll over an employer-sponsored plan or transfer an existing IRA without incurring early withdrawal penalties.'
		],
		imageUrl: `${appCloudfrontPath}/images/ira/woman-with-headphones.png`
	},
	{
		heading: 'Our platform',
		title: 'Retirement investing made easy',
		description: [
			"Opening an IRA with Fundrise is simple. Whether you're rolling over an existing retirement account or making a new contribution, you can begin investing in just minutes. Our web and mobile platforms make the process simple, with no complicated paperwork."
		],
		imageUrl: `${appCloudfrontPath}/images/ira/cell-phone-cafe.png`
	}
];

const contributionLimit = computed((): string => {
	return ira.contributionLimit ?? '';
});

const seniorContributionLimit = computed((): string => {
	return ira.seniorContributionLimit ?? '';
});

const taxYear = computed((): string => {
	return ira.taxYear ?? '';
});

const feeAmount = computed((): string => {
	return ira.feeAmount ?? '';
});

const annualThresholdLimit = computed((): string => {
	return ira.annualThresholdLimit ?? '';
});

const accountSizeThresholdLimit = computed((): string => {
	return ira.accountSizeThresholdLimit ?? '';
});

const iraHeroDetails = computed((): MarketingImageHero => {
	return {
		backgroundColor: 'bg-color-bg-secondary',
		title: 'Save for your retirement.<br/>Invest in your future.',
		description:
			'Strengthen your retirement savings by investing in high-quality private assets like real estate, private credit, and venture capital.',
		imageUrl: `${appCloudfrontPath}/images/ira/ira-hero-${appStore.isMobile ? 'mobile' : 'desktop'}.png`
	};
});

const faqContent = computed((): Array<QuestionItem> => {
	return [
		{
			question: 'What types of IRA accounts does Fundrise offer? ',
			answer: 'We offer Traditional and Roth IRAs.'
		},
		{
			question: 'How does pricing work?',
			answer: `Investors pay a ${currency(
				feeAmount.value,
				0
			)} annual fee for investing through an IRA, in addition to our standard fee structure. You can find a full description of our standard fee structure in our <a class="text-color-content-primary text-decoration-underline" target="_blank" href="${appBasePath}/oc?cta=Top%20Questions%20-%20Fees">offering circulars</a>.`
		},
		{
			question: 'How do I get the fee waiver?',
			answer: `Our IRA custodian, Inspira Financial Trust, LLC operates your IRA with a ${currency(feeAmount.value, 0)} annual fee. Earn a one-year fee waiver when you invest ${currency(annualThresholdLimit.value, 0)} or more, or secure an automatically recurring fee waiver when you achieve and maintain an account value over ${currency(accountSizeThresholdLimit.value, 0)}.`
		},
		{
			question: 'Is it possible to invest with funds in an existing retirement account?',
			answer: "You'll need to open an IRA and transfer the funds into the new account to invest with Fundrise. Before doing so, it's important to liquidate any non-liquid assets in your current account that you'd like to transfer. You can transfer only a portion of the account balance or the entire account, but it's important to keep in mind that your previous provider may charge a fee for moving funds out of the account."
		},
		{
			question: 'What is the limit on contributions to an IRA?',
			answer: `Contributions to an IRA are subject to annual limits established by the IRS. For tax year ${
				taxYear.value
			}, IRA contributions may not exceed the lesser of ${currency(contributionLimit.value, 0)} (${currency(
				seniorContributionLimit.value,
				0
			)} if you're age 50 or older) or your taxable compensation for the year. It's important to keep in mind that this yearly contribution limit applies across all IRAs in your name. For more information on IRA contribution limits, please visit the <a class="text-color-content-primary text-decoration-underline" href="https://www.irs.gov/retirement-plans/plan-participant-employee/retirement-topics-ira-contribution-limits" target="_blank">IRS website</a>.`
		},
		{
			question: 'Who is the custodian of Fundrise IRAs?',
			answer: 'Inspira Financial Trust, LLC is the custodian of assets in the individual retirement accounts opened through the Fundrise platform.'
		}
	];
});

function setup(): void {
	const metaTitle = 'IRA Investing';
	const metaDescription =
		'Strengthen your retirement savings by investing in high-quality private assets like real estate, private credit, and venture capital.';

	useHead({
		title: metaTitle,
		meta: [
			{ property: 'og:title', content: metaTitle },
			{ name: 'description', content: metaDescription },
			{ property: 'og:description', content: metaDescription }
		]
	});
}
setup();
</script>

<style lang="scss">
@use '../../../styles/utilities/respond-to.scss' as *;
@use '../../../styles/constants/_colors.scss' as *;

.ira-landing {
	.balance-text {
		text-wrap: balance;
		-webkit-text-wrap: balance;
	}

	.ira-cta-list {
		list-style-type: disc;

		li {
			display: list-item;
			margin-left: 2.5rem;
		}
	}

	.hero-img-wrapper {
		max-height: 500px;

		.hero-img {
			-webkit-mask-image: linear-gradient(
				to bottom,
				rgba(0, 0, 0, 1) 60%,
				rgba(0, 0, 0, 0.1) 80%,
				rgba(0, 0, 0, 0) 83%
			);
			mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 60%, rgba(0, 0, 0, 0.1) 80%, rgba(0, 0, 0, 0) 83%);
		}

		@include respond-to(smo) {
			max-height: 400px;
			.hero-img {
				-webkit-mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 60%, rgba(0, 0, 0, 0) 85%);
				mask-image: linear-gradient(to bottom, rgba(0, 0, 0, 1) 60%, rgba(0, 0, 0, 0) 85%);
			}
		}
	}

	.ratings-stars {
		&::before {
			background: linear-gradient(90deg, var(--star-background) var(--percent), gray(80) var(--percent));
			-webkit-background-clip: text;
			-webkit-text-fill-color: transparent;
			background-clip: text;
		}
	}
}
</style>
